import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { History, Trophy, XCircle, AlertTriangle, TrendingUp, TrendingDown, Minus } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function ClientHistoryPanel({ orgName }) {
  const [data, setData] = useState(null);

  useEffect(() => {
    const key = (orgName || '').trim();
    if (key.length < 2) { setData(null); return undefined; }
    const t = setTimeout(() => {
      axios.get(`${API_URL}/api/insights/client-history`, { params: { org: key }, withCredentials: true })
        .then(res => setData(res.data))
        .catch(() => setData(null));
    }, 500);
    return () => clearTimeout(t);
  }, [orgName]);

  if (!data) return null;
  const ests = data.estimates || [];
  const rfp = data.rfp || { won: 0, lost: 0, recent: [] };
  if (ests.length === 0 && rfp.won + rfp.lost === 0) return null;

  return (
    <div className="mb-6 rounded-lg border border-[#4ABFCF]/30 bg-[#4ABFCF]/5 p-4" data-testid="client-history-panel">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-[#0F2040] flex items-center gap-1.5">
          <History className="w-3.5 h-3.5 text-[#4ABFCF]" />
          We&apos;ve worked with this client before
        </p>
        {rfp.won + rfp.lost > 0 && (
          <span className="flex items-center gap-2 text-[10px]" data-testid="client-history-rfp-record">
            <span className="flex items-center gap-1 text-emerald-600 font-semibold"><Trophy className="w-3 h-3" />{rfp.won} won</span>
            <span className="flex items-center gap-1 text-rose-600 font-semibold"><XCircle className="w-3 h-3" />{rfp.lost} lost</span>
            <span className="text-slate-400">RFPs</span>
          </span>
        )}
      </div>
      {data.win_hint && (() => {
        const h = data.win_hint;
        const styles = {
          high: { cls: 'border-emerald-200 bg-emerald-50 text-emerald-700', Icon: TrendingUp, label: 'High' },
          moderate: { cls: 'border-amber-200 bg-amber-50 text-amber-700', Icon: Minus, label: 'Moderate' },
          low: { cls: 'border-rose-200 bg-rose-50 text-rose-700', Icon: TrendingDown, label: 'Low' },
        }[h.level];
        return (
          <div className={`mb-2 flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-[11px] font-medium ${styles.cls}`}
            data-testid="client-history-win-hint">
            <styles.Icon className="w-3.5 h-3.5 shrink-0" />
            Win likelihood: {styles.label} ({h.pct}%) — {h.wins} of {h.wins + h.losses} past pursuit{h.wins + h.losses === 1 ? '' : 's'} won
            {h.caution && <span className="font-normal opacity-80">· tempered by the pricing-risk flag</span>}
          </div>
        );
      })()}
      {rfp.pricing_risk && (
        <div className="mb-2 flex items-center gap-1.5 rounded-md border border-rose-200 bg-rose-50 px-2.5 py-1.5 text-[11px] text-rose-700 font-medium"
          data-testid="client-history-pricing-risk">
          <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
          Pricing risk: {rfp.pricing_losses} of {rfp.lost} lost RFP{rfp.lost === 1 ? '' : 's'} for this client
          {rfp.pricing_losses === 1 ? ' was' : ' were'} lost on pricing — price this estimate carefully.
        </div>
      )}
      {ests.length > 0 && (
        <table className="w-full text-[11px]">
          <thead>
            <tr className="text-[9px] text-slate-400 uppercase tracking-wide text-left border-b border-[#4ABFCF]/20">
              <th className="py-1 pr-2">Estimate</th><th className="py-1 pr-2">Date</th>
              <th className="py-1 pr-2 text-right">Value</th><th className="py-1 pr-2 text-right">Discount</th>
              <th className="py-1 pr-2">Status</th><th className="py-1">Sales lead</th>
            </tr>
          </thead>
          <tbody>
            {ests.map((e, i) => (
              <tr key={`${e.name}-${i}`} className="border-b border-[#4ABFCF]/10" data-testid={`client-history-row-${i}`}>
                <td className="py-1 pr-2 font-medium text-[#0F2040]">{e.name}</td>
                <td className="py-1 pr-2 text-slate-500">{e.created_at}</td>
                <td className="py-1 pr-2 text-right">{e.total_cost != null ? `$${Math.round(e.total_cost).toLocaleString()}` : '—'}</td>
                <td className={`py-1 pr-2 text-right font-semibold ${e.discount_pct > 0 ? 'text-rose-600' : 'text-slate-400'}`}>
                  {e.discount_pct != null ? `${e.discount_pct}%` : '—'}
                </td>
                <td className="py-1 pr-2 text-slate-500">{(e.status || '—').replace(/_/g, ' ')}</td>
                <td className="py-1 text-slate-500">{e.sales_rep || '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {(rfp.recent || []).length > 0 && (
        <div className="mt-2 flex flex-wrap gap-x-3 gap-y-1" data-testid="client-history-rfp-recent">
          {rfp.recent.map((r, i) => (
            <span key={`${r.name}-${i}`} className="text-[10px] text-slate-500">
              <span className={`font-semibold ${r.outcome === 'won' ? 'text-emerald-600' : 'text-rose-600'}`}>{r.outcome.toUpperCase()}</span>
              {' '}{r.name}
              {(r.loss_reasons || []).length > 0 && <span className="text-slate-400"> ({r.loss_reasons.join(', ')})</span>}
            </span>
          ))}
        </div>
      )}
      <p className="text-[9px] text-slate-400 mt-2">Use their history to pitch consistently — prior pricing, discounts given, and how past RFPs closed.</p>
    </div>
  );
}
